import { useMutation, useQuery } from '@tanstack/react-query';
import { List as F7List, ListItem } from 'framework7-react';
import { ComponentProps, FC, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { queryClient } from '@contexts/queryClient';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';
import mixpanel from 'mixpanel-browser';

import API from '@api/API';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { FeedEvent, useEventQuery, useUnauthorizedEventsQuery } from '@queries/EventQueries';
import {
  Series,
  SeriesBonusTip,
  useSeriesEntertainersQuery,
  useSeriesPostersReviewsQuery,
} from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { useRouter } from '@router/index';

import { formatCentsAmountToDollars } from '@utilities/formatCentsAmountToDollars';

import Button from '@components/buttons/Button';
import { DatePicker, DatePickerV2 } from '@components/form/DatePicker';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { List } from '@components/layout/List/List';
import { Popup } from '@components/layout/Popup/Popup';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { MostSupportedEntertainersGrid } from '@screens/entertainer/DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { useCurrentUserSupportedEntertainersCount } from '@screens/entertainer/SupportEntertainersScreen/SupportEntertainersScreen';

import { AddPosterPopup } from './AddPosterPopup';
import { BonusTipWeekendSection } from './BonusTipWeekendSection';
import dollarsSvgUrl from './dollars.svg';
import heartsSvgUrl from './hearts.svg';

export const PosterGrid: FC<{
  posters: (FeedEvent & { reviewsCount?: number })[];
  backUrl: string;
  emptyStateProps?: {
    isLoading: boolean;
  } & ComponentProps<typeof EmptyState>;
}> = ({ posters, backUrl, emptyStateProps }) => {
  const { navigate } = useRouter();
  const { currentUser } = useCurrentUser();

  const handleClick = (eventId: string) => {
    navigate(`/events/${eventId}/reviews`, { state: { back: backUrl } });
  };

  if (emptyStateProps?.isLoading) {
    return <Skeleton height={160} />;
  }

  if (!posters?.length && emptyStateProps) {
    return <EmptyState {...emptyStateProps} />;
  }

  return (
    <div
      className="d-grid"
      style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', margin: '0 -1rem' }}
    >
      {posters?.map((seriesPoster) => (
        <div
          key={seriesPoster._id}
          className="w-100"
          style={{ position: 'relative' }}
          onClick={() => {
            handleClick(seriesPoster._id);
          }}
        >
          <img
            src={seriesPoster.img}
            alt={seriesPoster.title}
            style={{
              width: '100%',
              height: '100%',
              aspectRatio: '4 / 5',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {seriesPoster.title === 'Title Not Provided' && currentUser?.isSuperAdmin && (
            <div
              className="d-flex gap-2 align-items-center"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'yellow',
                textShadow: '0 0 4px black',
              }}
            >
              <FontAwesomeIcon name="exclamation-triangle" />
            </div>
          )}
          <div
            className="d-flex gap-2 align-items-center"
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              color: 'white',
              textShadow: '0 0 4px black',
            }}
          >
            <FontAwesomeIcon name="comment" />
            <span>{seriesPoster.reviewsCount ?? 0}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export const VoteBonusTipWeekendSection: FC<{
  seriesBonusTip: SeriesBonusTip;
  tagSlug: string;
  backUrl: string;
}> = ({ seriesBonusTip, tagSlug, backUrl }) => {
  const { navigate } = useRouter();

  const formattedTitle = `${formatCentsAmountToDollars(seriesBonusTip?.payoutAmount ?? 10000)} Bonus Tip!`;

  return (
    <div
      className="relative"
      style={{
        background: 'rgba(255,255,255,.1)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      onClick={() => {
        navigate(`/noauth/series/${tagSlug}/bonus-tip-weekend`, { state: { back: backUrl } });
      }}
    >
      <div className="d-flex gap-2 align-items-center py-1">
        <div className="flex-shrink-0">
          <div className="ps-2" style={{ width: 60, height: 60 }}>
            <img
              src={dollarsSvgUrl}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                margin: '0 auto',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
        <div>
          <div>
            <strong>{formattedTitle}</strong>
          </div>
          <div style={{ opacity: 0.75, lineHeight: 1.3, fontSize: '.8rem' }}>
            <span>You support the shows. We support the cast with a bonus tip.</span>
          </div>
        </div>
        <div className="ml-auto px-3">
          <FontAwesomeIcon name="chevron-right" />
        </div>
      </div>
    </div>
  );
};

const BONUS_TIP_WEEKEND_REVIEW_SECTION_VISIBLE = false;

export const SeriesPostersReviews: FC<{
  series: Series;
  tagSlug: string;
  query: { posterFilter?: string };
  seriesBonusTip?: SeriesBonusTip;
}> = ({ series, seriesBonusTip, tagSlug, query }) => {
  const [posterFilter, setPosterFilter] = useState(query?.posterFilter ?? 'upcoming');
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const [isAddFlyerPopupVisible, setIsAddFlyerPopupVisible] = useState(false);
  const { areSeriesPostersReviewsFetched, seriesPosters = [] } =
    useSeriesPostersReviewsQuery(tagSlug);
  const { createAuthGateHandler } = useAuthGate();
  const { userIsLoggedIn } = useAppContext();
  const { supportEntertainersDashboardTile, badgeCount } = useCurrentUserSupportedEntertainersCount(
    series.city?._id,
  );

  const { entertainers: seriesEntertainers } = useSeriesEntertainersQuery(series._id);

  const top3SeriesEntertainers = useMemo(() => {
    return orderBy(
      seriesEntertainers?.filter(
        (seriesEntertainer) =>
          seriesEntertainer.avatar || seriesEntertainer.featuredMedia?.[0]?.image,
      ),
      (seriesEntertainer) => seriesEntertainer.trendingScore ?? 0,
      'desc',
    ).slice(0, 3);
  }, [seriesEntertainers]);

  const top3SeriesPosters = useMemo(
    () => orderBy(seriesPosters, ({ reviewsCount = 0 }) => reviewsCount, 'desc').slice(0, 3),
    [seriesPosters],
  );

  const bonusTipPosters = useMemo(() => {
    const bonusTipEleigibleShowDates = seriesBonusTip?.eligibleShowDates;

    if (bonusTipEleigibleShowDates?.startDate && bonusTipEleigibleShowDates?.endDate) {
      return seriesPosters.filter(
        ({ startDate }) =>
          dayjs(startDate).isSameOrAfter(bonusTipEleigibleShowDates.startDate) &&
          dayjs(startDate).isSameOrBefore(bonusTipEleigibleShowDates.endDate),
      );
    }

    return [];
  }, [
    seriesPosters,
    seriesBonusTip?.eligibleShowDates?.startDate,
    seriesBonusTip?.eligibleShowDates?.endDate,
  ]);

  const onContributorLogin = () => {
    createAuthGateHandler(() => {
      navigate(`/noauth/series/${tagSlug}/support-and-earn`);
    });
  };

  if (!areSeriesPostersReviewsFetched) {
    return (
      <div className="mt-2">
        <Skeleton height={300} />
      </div>
    );
  }

  const backUrl = `/noauth/series/denver-drag?tab=postersReviews&posterFilter=${posterFilter}`;

  const primaryCtas = [
    ['denver-drag', 'denver-queer-calendar'].includes(series.slug) && (
      <PrimaryCtaCard
        cta={{
          img: 'https://gaggl.app/api/public/uploads/2026/10/v1zzpetc6l.jpg',
          title: 'Denver Drag Showcase',
          description:
            'Join us on the 1st Friday of Denver Arts Week for a variety of performances around Denver!',
          url: `https://www.lavenderhilldenver.org/denver-drag-showcase`,
        }}
        track={{ event: 'Click Series Primary CTA' }}
      />
    ),
    seriesBonusTip?.payoutAmount && (
      <VoteBonusTipWeekendSection
        seriesBonusTip={seriesBonusTip}
        tagSlug={tagSlug}
        backUrl={backUrl}
      />
    ),
    <PrimaryCtaCard
      cta={{
        img: heartsSvgUrl,
        title: 'Support Local Performers',
        description:
          'Loved a recent look or show? Leave a little love. The best shout-outs make it into the weekly guide.',
        url: `/entertainers/support?cityId=${series.city?._id}`,
        onClick: () => {
          navigate(`/entertainers/support?cityId=${series.city?._id}`, {
            state: { back: backUrl },
          });
        },
        badgeCount: supportEntertainersDashboardTile ? badgeCount : 1,
      }}
      track={{ event: 'Click Series Primary CTA' }}
    />,
  ].filter(Boolean);

  return (
    <>
      {primaryCtas.length > 0 && <div className="mt-2 d-grid gap-2">{primaryCtas}</div>}

      <div className="mt-4">
        <TitleToolbar
          text="Top Recommended"
          size="md"
          infoButton={{
            title: 'Top Recommended Shows',
            text: "These shows have the most hype so they're the top recommended upcoming shows!",
          }}
        />
        <PosterGrid posters={top3SeriesPosters} backUrl={backUrl} />

        {top3SeriesEntertainers.length >= 3 && (
          <>
            <TitleToolbar
              text="Trending performers"
              size="md"
              infoButton={{
                title: 'Trending Performers',
                text: "These performers have recently received comments and likes so they're trending in the area!",
              }}
              action={
                <Button
                  variant="flat"
                  color="neutral"
                  size="sm"
                  endIcon={<FontAwesomeIcon name="chevron-right" />}
                  onClick={() => {
                    track('Click View All Trending Performers');
                    navigate(`/entertainers/support?cityId=${series.city?._id}`, {
                      state: { back: backUrl },
                    });
                  }}
                >
                  See all
                </Button>
              }
            />
            <MostSupportedEntertainersGrid
              entertainers={top3SeriesEntertainers}
              onClickEntertainer={(entertainerId) => {
                track('Click trending entertainer', { entertainerId });
                navigate(
                  `/entertainers/support?cityId=${series.city?._id}&entertainerId=${entertainerId}`,
                  {
                    state: { back: backUrl },
                  },
                );
              }}
            />
          </>
        )}

        {BONUS_TIP_WEEKEND_REVIEW_SECTION_VISIBLE && (
          <BonusTipWeekendSection
            seriesBonusTip={seriesBonusTip}
            isButtonDisabled={posterFilter === 'bonusTip'}
            onViewEligibleEvents={() => setPosterFilter('bonusTip')}
          />
        )}

        {posterFilter === 'upcoming' && (
          <>
            <TitleToolbar
              text="Upcoming Shows"
              size="md"
              action={
                <Button
                  roundness="rounded"
                  size="sm"
                  startIcon={<FontAwesomeIcon name="plus" />}
                  style={{ padding: '4px 10px', borderRadius: 4 }}
                  onClick={() => {
                    track('Click Add Flyer');
                    setIsAddFlyerPopupVisible(true);
                  }}
                >
                  Add Flyer
                </Button>
              }
            />
            <PosterGrid posters={seriesPosters} backUrl={backUrl} />
          </>
        )}

        {posterFilter === 'bonusTip' && (
          <>
            <TitleToolbar
              text="Bonus Tip Shows"
              size="md"
              action={
                <Button
                  roundness="rounded"
                  size="sm"
                  startIcon={<FontAwesomeIcon name="plus" />}
                  style={{ padding: '4px 10px', borderRadius: 4 }}
                  onClick={() => {
                    track('Click Add Flyer');
                    setIsAddFlyerPopupVisible(true);
                  }}
                >
                  Add Show
                </Button>
              }
            />

            <div className="my-2">
              <Button
                roundness="rounded"
                variant="smooth"
                color="neutral"
                onClick={() => setPosterFilter('upcoming')}
              >
                View All Upcoming Shows
              </Button>
            </div>

            <PosterGrid posters={bonusTipPosters} backUrl={backUrl} />
          </>
        )}

        {!userIsLoggedIn && (
          <div style={{ marginTop: 8 * 6 }}>
            <hr style={{ width: 40, margin: '0 auto' }} />
            <div className="mt-4">
              <Button
                roundness="rounded"
                color="neutral"
                variant="flat"
                onClick={onContributorLogin}
              >
                Trusted Supporter Log In
              </Button>
            </div>
          </div>
        )}

        <AddPosterPopup
          isVisible={isAddFlyerPopupVisible}
          onClose={() => setIsAddFlyerPopupVisible(false)}
          series={series}
          tagSlug={tagSlug}
        />
      </div>
    </>
  );
};
